import React, {createContext, useContext, ReactNode, useMemo} from 'react'

interface AccessibilityState {
    /** Indicates if the banner can be rendered or no */
    isAccessibilityBannerEnabled: boolean

    /** Contains usableNet script path */
    accessibilityScriptPath: string | null
}

const AccessibilityContext = createContext<AccessibilityState | undefined>(undefined)

/**
 * @name AccessibilityProvider
 *
 * @provider
 *
 * @description
 * Provides accessibility activation states and script path information
 * Tomorrow could be requested to activate only under profiling cookies, mantain inside CookieProvider
 *
 * @param {ReactNode} children
 * @param {string} accessibilityPrefs the path returned by the system preference
 *
 * @returns {JSX.Element} - Wrapped children.
 */
const AccessibilityProvider = ({children, accessibilityPrefs}: {children: ReactNode; accessibilityPrefs: string}) => {
    const accessibilityScriptPath = useMemo(() => accessibilityPrefs, [accessibilityPrefs])
    const isAccessibilityBannerEnabled = useMemo(() => !!accessibilityPrefs, [accessibilityPrefs])

    return <AccessibilityContext.Provider value={{isAccessibilityBannerEnabled, accessibilityScriptPath}}>{children}</AccessibilityContext.Provider>
}

/**
 * @name useAccessibilityState
 *
 * @hook
 *
 * @description
 * Custom hook that provides access to the accessibility state context.
 * This hook must be used within a AccessibilityProvider.
 *
 * @throws {Error} If the hook is used outside of a AccessibilityProvider.
 *
 * @returns {HeaderState} The current state from the accessibility context.
 */
const useAccessibilityState = (): AccessibilityState => {
    const context = useContext(AccessibilityContext)
    if (context === undefined) {
        return {
            isAccessibilityBannerEnabled: false,
            accessibilityScriptPath: '',
        }
    }
    return context
}

export {AccessibilityProvider, useAccessibilityState}
